/**
 * FormikGooglePlacesCombobox — Formik wrapper around GooglePlacesCombobox.
 *
 * Replaces: FormikGooglePlacesAutocomplete (MUI-based).
 *
 * Reads `${name}.display` as the display field and `${name}` as the
 * full address object. On place selection, writes the parsed address
 * back via setFieldValue, preserving the existing `id` and `name` fields.
 *
 * Migration mapping:
 *   <FormikGooglePlacesAutocomplete
 *     name="worksiteLocation"
 *     setFieldValue={setFieldValue}
 *     address={values.worksiteLocation}
 *     isCityStateBoolean={false}
 *     initialEditValues={initialValues}
 *     disabled={false}
 *   />
 *     ->
 *   <FormikGooglePlacesCombobox
 *     name="worksiteLocation"
 *     setFieldValue={setFieldValue}
 *     isCityState={false}
 *     disabled={false}
 *   />
 */
import {useField} from 'formik'
import React, {useCallback} from 'react'
import FormikInput from '~/src/design-system-shims/formik/FormikInput'
import {GooglePlacesCombobox} from '~/src/design-system-shims/GooglePlacesCombobox'
import type {GooglePlacesComboboxProps} from '~/src/design-system-shims/GooglePlacesCombobox'
import type {IBaseAddress} from '~/src/models/baseAddress'

/**
 * SHR-3921: compose the one-line `display` from structured parts, inserting the
 * manual unit (line2) right after the street. Google autocomplete never returns a
 * subpremise, and nearly every read-only surface renders `address.display`, so the
 * unit has to be folded in here or it stays invisible. Called only on user actions
 * (place-select + unit edits), never on mount, so persisted addresses keep their
 * stored display until the user actually changes something.
 */
export function composeAddressDisplay(a: {
  line1?: string
  line2?: string
  city?: string
  state?: string
  zipCode?: string
}): string {
  const street = [a.line1, a.line2]
    .map((p) => p?.trim())
    .filter(Boolean)
    .join(', ')
  const region = [a.city, a.state]
    .map((p) => p?.trim())
    .filter(Boolean)
    .join(', ')
  const tail = [region, a.zipCode?.trim()].filter(Boolean).join(' ')
  return [street, tail].filter(Boolean).join(', ')
}

export interface FormikGooglePlacesComboboxProps
  extends Omit<
    GooglePlacesComboboxProps,
    'value' | 'onPlaceSelect' | 'onValueChange' | 'errorText' | 'isCityState'
  > {
  /** Formik field name — maps to an address object (e.g. "worksiteLocation") */
  name: string
  /** Formik setFieldValue — used to write the full address object */
  setFieldValue: (field: string, value: unknown) => void
  /** When true, searches for cities only; when false, searches full addresses */
  isCityState?: boolean
  /**
   * Legacy alias for isCityState to ease migration.
   * If both are provided, isCityState takes precedence.
   */
  isCityStateBoolean?: boolean
  /**
   * Legacy: current address value — ignored in the new implementation
   * since the component reads from Formik directly.
   */
  address?: IBaseAddress | string | number | boolean | null | undefined
  /**
   * Legacy: initial edit values — ignored in the new implementation.
   */
  initialEditValues?: unknown
  /** Legacy: MUI error prop — absorbed for migration compat */
  error?: boolean
  /** Legacy: MUI inputProps — absorbed for migration compat */
  inputProps?: unknown
  /**
   * SHR-3921: opt out of the Unit / Apt / Suite field for this usage. Defaults to
   * shown in full-address mode; set on forms where a subpremise doesn't apply
   * (e.g. school / employer addresses).
   */
  hideUnitField?: boolean
}

export const FormikGooglePlacesCombobox: React.FC<FormikGooglePlacesComboboxProps> = ({
  name,
  setFieldValue,
  isCityState,
  isCityStateBoolean,
  address: _address,
  initialEditValues: _initialEditValues,
  error: _error,
  inputProps: _inputProps,
  hideUnitField,
  disabled,
  placeholder,
  label,
  helperText,
  size,
  'data-testid': testId,
  ...rest
}) => {
  // Resolve the city-state flag: isCityState takes precedence, fall back to legacy alias
  const resolvedIsCityState = isCityState ?? isCityStateBoolean ?? false

  // useField on the display subfield for validation/touched state
  const [displayField, displayMeta, displayHelpers] = useField({name: `${name}.display`})
  // useField on the full address object to read existing id/name
  const [{value: addressObj}] = useField({name})

  const hasError = displayMeta.touched && Boolean(displayMeta.error)
  const errorText = hasError ? displayMeta.error : undefined

  // Also show "Required" for full-address mode when line1 is missing
  const derivedErrorText =
    errorText ??
    (!resolvedIsCityState && displayMeta.touched && !addressObj?.line1 ? 'Required' : undefined)

  const handlePlaceSelect = useCallback(
    (parsedAddress: IBaseAddress) => {
      // SHR-3921: prefer a unit Google actually returned (adr "extended-address" /
      // subpremise, on parsedAddress.line2 when present); otherwise keep the
      // manually-entered one across re-selection so replacing the object won't drop it.
      const unit = parsedAddress.line2?.trim() ? parsedAddress.line2 : addressObj?.line2
      setFieldValue(name, {
        ...parsedAddress,
        line2: unit,
        // Fold the unit into `display` so it shows on the many surfaces that render
        // address.display. No unit → keep Google's display byte-for-byte.
        display: unit?.trim()
          ? composeAddressDisplay({...parsedAddress, line2: unit})
          : parsedAddress.display,
        id: addressObj?.id,
        ...(addressObj?.name && {name: addressObj.name}),
      })
    },
    [setFieldValue, name, addressObj?.id, addressObj?.name, addressObj?.line2],
  )

  const handleValueChange = useCallback(
    (display: string) => {
      displayHelpers.setValue(display)
      displayHelpers.setTouched(true)
    },
    [displayHelpers],
  )

  // SHR-3921: address + Unit share one inline row (≈80/20 + 5px gap) so the unit
  // doesn't stack and overflow tight layouts; min-width:0 lets the address shrink.
  const showUnit = !resolvedIsCityState && !hideUnitField
  return (
    <div
      onBlur={() => {
        displayHelpers.setTouched(true)
      }}
      style={showUnit ? {display: 'flex', gap: '5px', alignItems: 'flex-start'} : undefined}>
      <div style={showUnit ? {flex: '1 1 auto', minWidth: 0} : undefined}>
        <GooglePlacesCombobox
          name={name}
          value={displayField.value ?? ''}
          onPlaceSelect={handlePlaceSelect}
          onValueChange={handleValueChange}
          isCityState={resolvedIsCityState}
          placeholder={placeholder}
          disabled={disabled}
          size={size}
          label={label}
          errorText={derivedErrorText}
          helperText={!derivedErrorText ? helperText : undefined}
          data-testid={testId ?? 'formik-google-places-combobox'}
          {...rest}
        />
      </div>
      {/* SHR-3921: Unit / Apt / Suite — full-address mode only (city-state searches
          have no street to attach a unit to). Stored on the same address object as
          line2. Optional; leaving it blank keeps today's behaviour.

          STFFLG-3925: the unit carries NO visible label, deliberately. Every caller
          renders its own label ("Company Address", "EOR Billing Address", …) OUTSIDE
          this component and above the whole row, so the address column has no in-row
          label to sit beside. A visible label here would therefore be the only thing
          in the row consuming label height, pushing the unit input exactly one
          label-height below the address input on every form that uses this — which
          was the reported misalignment. The placeholder plus aria-label name the
          field instead, and both inputs now share one line. */}
      {showUnit && (
        <div style={{flex: '0 0 20%'}}>
          <FormikInput
            name={`${name}.line2`}
            aria-label="Unit / Apt / Suite"
            placeholder="e.g. Apt 4B (optional)"
            // SHR-3921: a unit is meaningless without a street, so the field stays
            // disabled until an address has been selected (line1 present).
            disabled={disabled || !addressObj?.line1}
            helperText={!addressObj?.line1 ? 'Select an address first' : undefined}
            size={size}
            data-testid={`${testId ?? 'formik-google-places-combobox'}-line2`}
            onChange={(e) => {
              // SHR-3921: keep `display` in sync as the unit is typed/cleared. FormikInput
              // calls this before its own field.onChange, so e.target.value is the new unit.
              // Guard on an existing base address so typing a unit first can't clobber the
              // in-progress search text with a unit-only string.
              if (addressObj?.line1) {
                setFieldValue(
                  `${name}.display`,
                  composeAddressDisplay({...addressObj, line2: e.target.value}),
                )
              }
            }}
          />
        </div>
      )}
    </div>
  )
}

export default FormikGooglePlacesCombobox
